import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { ProduccionCabecera, PlanificacionSemanalRow } from '@/types'
import type { TimeTrack } from '@/utils/planningTimeTracks'
import { fmtUds, pct, sd } from '@/utils/planningFormatters'
import { GanttRow } from '@/components/planning/GanttRow'
import { cuentaComoRotura } from '@/utils/estadoMaterial'

/** Ancho del nombre de ruta dentro de la cabecera del grupo. Es algo menor que
 *  INFO_COL_WIDTH porque el botón que lo contiene ya aporta padding y el chevron. */
const ROUTE_NAME_WIDTH = 236

/**
 * Grupo colapsable de producciones de una misma ruta principal, con sus barras de
 * avance y de material bloqueado. Agnóstico al rango temporal: sólo pasa el
 * `track` a cada `GanttRow`.
 */
export function RutaSection({ ruta, producciones, planRows, track, dark, onSelectComponent, highlightedProdId, globalExpanded, isEmpresaAll, autoAbrirProds, resalta, busquedaNonce }: {
  ruta: string
  producciones: ProduccionCabecera[]
  planRows: PlanificacionSemanalRow[]
  track: TimeTrack
  dark: boolean
  onSelectComponent: (item: PlanificacionSemanalRow) => void
  highlightedProdId?: string | null
  globalExpanded: boolean
  isEmpresaAll: boolean
  /** OPs que han salido por un componente: abren solas su subtabla. */
  autoAbrirProds?: Set<string>
  /** Que componentes van resaltados dentro de cada subtabla. */
  resalta?: ((item: PlanificacionSemanalRow) => boolean) | null
  /** Firma de la busqueda. Al cambiar se olvida el colapso manual del grupo. */
  busquedaNonce?: string
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(globalExpanded)

  useEffect(() => { setExpanded(globalExpanded) }, [globalExpanded])

  // Un grupo cerrado a mano esconderia las coincidencias de la busqueda nueva,
  // asi que al cambiar el filtro vuelve a abrirse. Despues el usuario puede
  // volver a colapsarlo.
  useEffect(() => {
    if (busquedaNonce) setExpanded(true)
  }, [busquedaNonce])

  useEffect(() => {
    if (highlightedProdId && producciones.some(p => p.produccion === highlightedProdId)) {
      setExpanded(true)
    }
  }, [highlightedProdId, producciones])

  const planByProd = useMemo(() => {
    const map = new Map<string, PlanificacionSemanalRow[]>()
    planRows.forEach(r => { const l = map.get(r.prodid) ?? []; l.push(r); map.set(r.prodid, l) })
    return map
  }, [planRows])

  const totalU     = producciones.reduce((s, p) => s + Number(p.unidades), 0)
  const completedU = producciones.reduce((s, p) => s + Number(p.unidades) - Number(p.unidadesPendientes), 0)
  const avPct      = pct(completedU, totalU)

  const statusCounts = useMemo(() => {
    const counts: Record<number, number> = {}
    producciones.forEach(p => { counts[p.status] = (counts[p.status] ?? 0) + 1 })
    return counts
  }, [producciones])

  const prodsRoturaSet   = new Set(planRows.filter(r => cuentaComoRotura(r)).map(r => r.prodid))
  const prodsRoturaCount = prodsRoturaSet.size
  const totalConsumoRuta = planRows.reduce((s, r) => s + r.consumoEstimado, 0)
  const totalShortfall   = planRows.filter(r => cuentaComoRotura(r)).reduce((s, r) => s + Math.max(0, r.pendiente), 0)
  const bloqPct          = pct(totalShortfall, totalConsumoRuta)

  const shortfallAbs = planRows
    .filter(r => cuentaComoRotura(r) && r.pendiente > 0)
    .reduce((s, r) => s + r.pendiente, 0)

  const totalOperarios = producciones.reduce((s, p) => s + Number(p.cantidadOperarios || 0), 0)

  const hdrBg  = dark ? 'bg-slate-800/50 hover:bg-slate-800/80' : 'bg-slate-50/80 hover:bg-slate-100/80'
  const border = dark ? 'border-white/6'  : 'border-slate-200'
  const txtM   = dark ? 'text-slate-100'  : 'text-slate-800'
  const txtMut = dark ? 'text-slate-400'  : 'text-slate-500'

  return (
    <div className={`rounded-xl overflow-hidden border mb-2 ${dark ? 'border-white/8 bg-slate-900' : 'border-slate-200 bg-white shadow-sm'}`}>
      <button
        onClick={() => setExpanded(v => !v)}
        className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors border-b ${hdrBg} ${border}`}
      >
        <span className={`text-[11px] w-3 shrink-0 ${txtMut}`}>{expanded ? '▾' : '▸'}</span>

        {/* Nombre de ruta + mini barra de estados */}
        <div className="shrink-0" style={{ width: ROUTE_NAME_WIDTH }}>
          <span className={`text-[12px] font-black ${txtM} truncate block`}>{ruta}</span>
          <div className="flex h-1 rounded-full overflow-hidden mt-1 gap-px">
            {[0,1,2,3,4,5].map(st => {
              const count = statusCounts[st] ?? 0
              if (!count) return null
              return (
                <div
                  key={st}
                  className={`h-full ${sd(st).bar} opacity-80`}
                  style={{ width: `${pct(count, producciones.length)}%`, minWidth: 3 }}
                  title={`${t('planning.status.'+st)}: ${count}`}
                />
              )
            })}
          </div>
        </div>

        {/* Barras de progreso */}
        <div className="flex-1 flex flex-col gap-1 min-w-0">
          {/* Avance */}
          <div className="flex items-center gap-2">
            <span className={`text-[9px] w-14 shrink-0 ${txtMut}`}>{t('planning.gantt.advance')}</span>
            <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${dark ? 'bg-slate-700' : 'bg-slate-200'}`}>
              <div
                className={`h-full rounded-full ${avPct >= 75 ? 'bg-emerald-400' : avPct >= 40 ? 'bg-sky-400' : 'bg-slate-400'}`}
                style={{ width: `${avPct}%` }}
              />
            </div>
            <span className={`text-[10px] font-semibold tabular-nums w-8 text-right shrink-0 ${txtMut}`}>{avPct}%</span>
          </div>
          {/* Mat. bloq. */}
          {bloqPct > 0 && (
            <div className="flex items-center gap-2">
              <span className={`text-[9px] w-14 shrink-0 ${dark ? 'text-red-400' : 'text-red-500'}`}>{t('planning.gantt.matBlocked')}</span>
              <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${dark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                <div className="h-full rounded-full bg-red-400" style={{ width: `${bloqPct}%` }} />
              </div>
              <span className={`text-[10px] font-semibold tabular-nums w-8 text-right shrink-0 ${dark ? 'text-red-400' : 'text-red-500'}`}>{bloqPct}%</span>
              {shortfallAbs > 0 && (
                <span className={`text-[9px] tabular-nums shrink-0 ${dark ? 'text-red-500' : 'text-red-400'}`}>
                  ({fmtUds(shortfallAbs)} u.)
                </span>
              )}
            </div>
          )}
        </div>

        <div className={`text-[11px] font-semibold ${txtM} shrink-0 tabular-nums`}>
          {producciones.length} prod.
          {prodsRoturaCount > 0 && <span className="ml-1.5 text-red-500 text-[10px] font-bold">⚠{prodsRoturaCount}</span>}
        </div>
        {totalOperarios > 0 && (
          <div className={`text-[11px] ${txtMut} shrink-0 tabular-nums ml-1`}>{totalOperarios} op.</div>
        )}
        <div className={`text-[11px] ${txtMut} shrink-0 tabular-nums ml-3`}>{fmtUds(totalU)} u</div>
      </button>

      {expanded && producciones.map(prod => (
        <GanttRow
          key={prod.produccion}
          prod={prod}
          items={planByProd.get(prod.produccion) ?? []}
          track={track}
          dark={dark}
          onSelectComponent={onSelectComponent}
          highlighted={highlightedProdId === prod.produccion}
          isEmpresaAll={isEmpresaAll}
          autoAbrir={autoAbrirProds?.has(prod.produccion)}
          resalta={resalta}
          busquedaNonce={busquedaNonce}
        />
      ))}
    </div>
  )
}
